import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { HeartHandshake } from 'lucide-react'
import { api, ApiError } from '../../api/client'
import { Button } from '../../components/ui/Button'
import { DateField } from '../../components/ui/DatePicker'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { usePaywallStore } from '../../store/paywall.store'
import { useSession } from '../../store/user.store'
import { formatBirthDate } from '../../lib/date-format'

export function CompatIndexRoute() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const showPaywall = usePaywallStore((s) => s.show)
  const lang = (useSession((s) => s.user?.language) ?? 'ru') as 'ru' | 'en'
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [date, setDate] = useState('')

  const listQuery = useQuery({
    queryKey: ['compat', 'list'],
    queryFn: () => api.listCompats(),
  })

  const createMutation = useMutation({
    mutationFn: () =>
      api.createCompatTeaser({ partnerName: name.trim(), partnerBirthDate: date }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['compat', 'list'] })
      setShowForm(false)
      setName('')
      setDate('')
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        showPaywall({
          reason: 'feature_locked',
          feature: 'compat_full',
        })
      }
    },
  })

  return (
    <div className="flex flex-col gap-5 pb-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl text-sage-800">{t('tabs.compat')}</h1>
        <p className="text-sm text-sage-600">{t('compat.intro')}</p>
      </header>

      {!showForm && (
        // Styled like the home QuickAction cards (blush accent for compat) so
        // the "create new" CTA reads as a friendly entry point, not a heavy
        // button sitting on top of the existing list.
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-3 rounded-2xl bg-[#fbe5e6] px-4 py-3 ring-1 ring-[#f0bfb7]/60 transition hover:bg-[#f7d4d6] active:scale-[0.98]"
        >
          <HeartHandshake className="h-7 w-7 text-copper" strokeWidth={1.6} aria-hidden />
          <span className="flex flex-col items-start">
            <span className="font-serif text-sage-800">{t('compat.new')}</span>
            <span className="text-xs text-sage-600">{t('compat.intro')}</span>
          </span>
        </button>
      )}

      {showForm && (
        <div className="flex flex-col gap-3 rounded-3xl bg-white/70 p-5 ring-1 ring-sage-200/60">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('compat.form_partner_name')}
            className="rounded-xl border border-sage-200 bg-white px-3 py-2 text-sage-800 outline-none focus:border-sage-500"
          />
          <DateField
            value={date}
            onChange={setDate}
            placeholder={t('compat.form_partner_date')}
            minAge={0}
            maxAge={100}
          />
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowForm(false)}>
              {t('actions.back')}
            </Button>
            <Button
              loading={createMutation.isPending}
              onClick={() => createMutation.mutate()}
              disabled={name.trim().length < 1 || !/^\d{4}-\d{2}-\d{2}$/.test(date)}
            >
              {t('compat.calc')}
            </Button>
          </div>
          {createMutation.error && (
            <p className="text-sm text-copper">{(createMutation.error as Error).message}</p>
          )}
        </div>
      )}

      <section>
        {listQuery.isLoading && <LoadingDots compact />}
        {listQuery.isError && <ErrorState onRetry={() => listQuery.refetch()} />}
        {listQuery.data && listQuery.data.length > 0 && (
          <ul className="flex flex-col gap-3">
            {listQuery.data.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/compat/${c.id}`}
                  className="block rounded-2xl bg-white/60 p-4 ring-1 ring-sage-200/60 transition hover:bg-white hover:shadow-sm active:scale-[0.99]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-col">
                      <span className="font-serif text-lg text-sage-800 truncate">{c.partnerName}</span>
                      <span className="text-xs text-sage-500">
                        {formatBirthDate(c.partnerBirthDate, lang)}
                      </span>
                    </div>
                    <span className="shrink-0 font-serif text-2xl text-copper">
                      {t('compat.score', { score: c.score.score })}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-sage-600">{c.teaserText}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
